import React, { ReactElement, useCallback, useMemo, useState } from 'react';

import { Checkbox, Select, RadioButton, Button } from 'src/ui/components';

import { ResponseType } from 'src/features/PersonRepository/types/QuestionsTabType';
import { usePersonRepoStore } from 'src/features/PersonRepository/store/personRepoStore';
import { disableQnText } from 'src/features/PersonRepository/utils/personRepoConstants';

export const choiceTypeOptions = {
  radio: 0,
  checkbox: 1,
  dropdown: 2,
};

export function ChoiceEdit({
  question,
}: {
  question: ResponseType;
}): ReactElement {
  const qnTempId = useMemo(() => {
    return question?.TemplateId;
  }, [question]);

  //gets type of choice question - radio,checkbox,dropdwn
  const choiceType = useMemo(() => {
    return question.TypeValue;
  }, [question]);

  const updateChoice = usePersonRepoStore(
    useCallback(state => state?.updateChoice, []),
  );

  const choiceObjectValue = usePersonRepoStore(
    React.useCallback(
      state => state?.formValues.Poc.Choice?.[qnTempId],
      [qnTempId],
    ),
  );

  const choiceDropDownOptions = useMemo(() => {
    const objToAppend = { value: -1, label: 'Select one' };
    if (choiceType === choiceTypeOptions.dropdown) {
      const requiredQn = question?.Options?.slice()
        ?.sort((a: any, b: any) =>
          a.Order === b.Order
            ? a.ItemLabel.localeCompare(b.ItemLabel)
            : a.Order - b.Order,
        )
        .map(item => {
          return {
            value: item.Id || 0,
            label: item?.ItemLabel || '',
          };
        });
      if (requiredQn) requiredQn.splice(0, 0, objToAppend);
      return requiredQn;
    }
  }, [choiceType, question?.Options]);

  const processedValue = () => {
    const data =
      choiceObjectValue &&
      question?.Options?.find(item => item?.ItemName === choiceObjectValue[0]);

    return data;
  };

  const handleCheck = (label: string | undefined) => {
    const updatedCheckedValues = choiceObjectValue
      ? [...choiceObjectValue]
      : [];
    const selectedLabels = updatedCheckedValues?.includes(label)
      ? updatedCheckedValues?.filter(
          (name: string | undefined) => name !== label,
        )
      : [...(updatedCheckedValues ?? []), label];
    updateChoice([...selectedLabels], qnTempId);
    return selectedLabels;
  };

  const handleRadio = (label: string | undefined) => {
    updateChoice([label], qnTempId);
  };

  const handleDropDownChange = (label: string | undefined) => {
    if (label) updateChoice([label], qnTempId);
    else updateChoice([], qnTempId);
  };

  const onClear = (templateId: number | undefined) => {
    if (templateId) {
      updateChoice([], templateId);
    }
  };

  const shouldDisableChoice = useMemo(() => {
    return question?.Options?.some(option => option.AccessTypeId === 1);
  }, [question?.Options]);

  return (
    <>
      <div className="container mx-auto flex gap-[1ch] border-b border-gray-100 bg-gray-50 dark:border-neutral-800 dark:bg-neutral-900/40 sm:justify-end">
        <Button
          variant="link"
          size="small"
          className="grow sm:grow-0"
          disabled={shouldDisableChoice}
          onClick={() => onClear(qnTempId)}
        >
          Clear
        </Button>
      </div>
      {choiceType === choiceTypeOptions.dropdown && choiceDropDownOptions && (
        <dd className="flex gap-2  border-b dark:border-dark-medium">
          <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
            {question?.FieldLabel}
          </h4>
          <div className="grid w-4/6 grid-cols-1 gap-x-6 gap-y-3 px-3 py-2 md:grid-cols-2">
            <Select
              id="choice-dropDown"
              options={choiceDropDownOptions}
              title={shouldDisableChoice ? disableQnText : ''}
              defaultSelected={
                choiceObjectValue === undefined ? 'Select one' : ''
              }
              value={processedValue()?.Id ?? ''}
              disabled={shouldDisableChoice}
              onChange={e => {
                const input = question?.Options?.find(
                  item => item?.Id === parseInt(e.target.value),
                );
                handleDropDownChange(input?.ItemName);
              }}
            />
          </div>
        </dd>
      )}

      {choiceType === choiceTypeOptions.radio && (
        <dd className="flex gap-2  border-b dark:border-dark-medium">
          <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
            {question?.FieldLabel}
          </h4>
          <div className="w-4/6 columns-2 gap-5 space-y-3 px-3 py-2">
            {question?.Options?.slice()
              ?.sort((a: any, b: any) =>
                a.Order === b.Order
                  ? a.ItemLabel.localeCompare(b.ItemLabel)
                  : a.Order - b.Order,
              )
              ?.map((rdItem, index) => {
                return (
                  <RadioButton
                    className="w-full"
                    key={`radioQn${index}${qnTempId}`}
                    id={rdItem?.Id?.toString()}
                    title={shouldDisableChoice ? disableQnText : ''}
                    label={`${rdItem.ItemLabel}`}
                    value={rdItem.ItemName}
                    checked={rdItem?.ItemName === choiceObjectValue?.[0]}
                    disabled={shouldDisableChoice}
                    onChange={() => handleRadio(rdItem?.ItemName)}
                  />
                );
              })}
          </div>
        </dd>
      )}

      {choiceType === choiceTypeOptions.checkbox && (
        <dd className="flex gap-2  border-b dark:border-dark-medium">
          <h4 className="w-1/3 px-3 py-2 text-sm font-medium">
            {question?.FieldLabel}
          </h4>
          <div className="w-4/6 columns-2 gap-5 space-y-3 px-3 py-2">
            {question?.Options?.map((rdItem, index) => (
              <Checkbox
                className="w-full"
                key={`qnChoiceCheckbox${index}${qnTempId}`}
                id={rdItem.Id.toString()}
                // name="checkboxTypeChoice"
                label={rdItem?.ItemLabel}
                checked={
                  choiceObjectValue?.includes(rdItem?.ItemName) ? true : false
                }
                title={shouldDisableChoice ? disableQnText : ''}
                disabled={shouldDisableChoice}
                onChange={() => {
                  handleCheck(rdItem?.ItemName);
                }}
              />
            ))}
          </div>
        </dd>
      )}
    </>
  );
}
